import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useGlobalStore } from '../stores/global-store'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { storeToRefs } from 'pinia'
import { User, UserRoles } from '@/services/api/types/user'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

declare module 'vue-router' {
  interface RouteMeta {
    // roles to restrict to only selected user roles - can be undefined
    roles?: UserRoles[]
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:catchAll(.*)',
    redirect: { path: 'admin/rides' },
  },
  {
    name: 'feedback',
    path: '/feedback',
    component: () => import('../pages/feedback/Feedback.vue'),
  },
  {
    name: 'ride',
    path: '/ride/:ride_id',
    component: () => import('../pages/ride/[ride_id].vue'),
  },
  {
    name: 'accept-driver',
    path: '/accept/driver',
    component: () => import('../pages/invite/AcceptInvitation.vue'),
  },
  {
    name: 'accept-invitation',
    path: '/accept-invitation',
    component: () => import('../pages/invite/AcceptInvitationByTemplate.vue'),
  },
  {
    name: 'join-company',
    path: '/join/:code',
    component: () => import('../pages/invite/JoinCompanyByCode.vue'),
  },
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    children: [
      {
        name: 'profile',
        path: '/profile',
        component: () => import('../pages/profile/Profile.vue'),
      },
      {
        name: 'phone-requests',
        path: 'phone-requests',
        component: () => import('../pages/admin/phone-requests/PhoneRequestsPage.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'drivers',
        path: 'drivers',
        component: () => import('../pages/admin/drivers/Drivers.vue'),
        meta: {
          roles: [
            UserRoles.BusinessPartner,
            UserRoles.RideSupport,
            UserRoles.Supervisor,
            UserRoles.Admin,
            UserRoles.Owner,
          ] as UserRoles[],
        },
      },
      {
        name: 'driver-performance',
        path: 'drivers/performance',
        component: () => import('../pages/admin/drivers/DriverPerformance.vue'),
        meta: {
          roles: [UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'driver-details',
        path: 'drivers/:driverId',
        component: () => import('../pages/admin/drivers/Drivers.vue'),
        meta: {
          roles: [
            UserRoles.BusinessPartner,
            UserRoles.RideSupport,
            UserRoles.Supervisor,
            UserRoles.Admin,
            UserRoles.Owner,
          ] as UserRoles[],
        },
      },
      {
        name: 'fleet',
        path: 'fleet',
        component: () => import('../pages/admin/fleet/Fleet.vue'),
        meta: {
          roles: [
            UserRoles.BusinessPartner,
            UserRoles.RideSupport,
            UserRoles.Supervisor,
            UserRoles.Admin,
            UserRoles.Owner,
          ] as UserRoles[],
        },
      },
      {
        name: 'fleet-details',
        path: 'fleet/:carId',
        component: () => import('../pages/admin/fleet/Fleet.vue'),
        meta: {
          roles: [
            UserRoles.BusinessPartner,
            UserRoles.RideSupport,
            UserRoles.Supervisor,
            UserRoles.Admin,
            UserRoles.Owner,
          ] as UserRoles[],
        },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/admin/users/Users.vue'),
        meta: {
          roles: [UserRoles.BusinessPartner, UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'telegram-users',
        path: 'telegram-users',
        component: () => import('../pages/admin/users/TelegramUsers.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'companies',
        path: 'companies',
        component: () => import('../pages/admin/companies/Companies.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'company',
        path: 'company',
        component: () => import('../pages/admin/companies/Companies.vue'),
        meta: {
          roles: [UserRoles.BusinessPartner, UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'applications',
        path: 'applications',
        component: () => import('../pages/admin/applications/Applications.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner, UserRoles.BusinessPartner] as UserRoles[],
        },
      },
      {
        name: 'application-details',
        path: 'applications/:applicationId',
        component: () => import('../pages/admin/applications/Applications.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner, UserRoles.BusinessPartner] as UserRoles[],
        },
      },
      {
        name: 'company-applications',
        path: 'company-applications',
        component: () => import('../pages/admin/company-applications/CompanyApplications.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner] as UserRoles[],
        },
      },
      {
        name: 'invoices',
        path: 'invoices',
        component: () => import('../pages/admin/invoices/Invoices.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner, UserRoles.BusinessPartner] as UserRoles[],
        },
      },
      {
        name: 'invoices-new',
        path: 'invoices/new',
        component: () => import('../pages/admin/invoices/NewInvoice.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner, UserRoles.BusinessPartner] as UserRoles[],
        },
      },
      {
        name: 'invoice-details',
        path: 'invoices/:invoiceId',
        component: () => import('../pages/admin/invoices/Invoices.vue'),
        meta: {
          roles: [UserRoles.Admin, UserRoles.Owner, UserRoles.BusinessPartner] as UserRoles[],
        },
      },
      {
        name: 'rides',
        path: 'rides',
        children: [
          {
            name: 'rides-list',
            path: '',
            component: () => import('../pages/admin/rides/pages/RidesList.vue'),
            meta: {
              transition: 'slide',
              roles: [
                UserRoles.Driver,
                UserRoles.RideSupport,
                UserRoles.Supervisor,
                UserRoles.Admin,
                UserRoles.Owner,
                UserRoles.BusinessPartner,
                UserRoles.Broker,
              ] as UserRoles[],
            },
          },
          {
            name: 'rides-list-ride',
            path: ':rideId',
            component: () => import('../pages/admin/rides/pages/RidesList.vue'),
            meta: {
              transition: 'slide',
              roles: [
                UserRoles.Driver,
                UserRoles.RideSupport,
                UserRoles.Supervisor,
                UserRoles.Admin,
                UserRoles.Owner,
                UserRoles.BusinessPartner,
                UserRoles.Broker,
              ] as UserRoles[],
            },
          },
          {
            name: 'rides-new',
            path: 'new',
            component: () => import('../pages/admin/rides/pages/NewRide.vue'),
            meta: {
              transition: 'slide',
              roles: [
                UserRoles.RideSupport,
                UserRoles.Supervisor,
                UserRoles.Admin,
                UserRoles.Owner,
                UserRoles.BusinessPartner,
                UserRoles.Broker,
              ] as UserRoles[],
            },
          },
        ],
      },
      {
        path: '/confirmation',
        // component: AuthLayout,
        children: [
          {
            name: 'confirmation',
            path: '',
            component: () => import('../pages/confirmation/Confirmation.vue'),
          },
        ],
      },
    ],
  },
  {
    path: '/pay/:ride_id',
    name: 'pay',
    component: () => import('../pages/admin/rides/pages/Payment.vue'),
  },
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('../pages/onboarding/Onboarding.vue'),
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/login/Login.vue'),
      },
    ],
  },
  {
    path: '/business',
    name: 'business',
    component: () => import('../pages/business/BusinessPartnerOnboarding.vue'),
  },
  {
    path: '/business/company-application',
    name: 'company-application',
    component: () => import('../pages/business/CompanyApplicationForm.vue'),
  },
  {
    path: '/join',
    redirect: { path: '/business/company-application' },
  },

  {
    name: '403',
    path: '/oops',
    component: () => import('../pages/403/index.vue'),
  },
  {
    name: 'who_am_i',
    path: '/who_am_i',
    redirect: () => {
      return { name: 'profile' }
    },
    component: () => import('../pages/403/index.vue'),
    children: [],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      document.querySelector('.app-layout__page')?.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to, from, next) => {
  NProgress.start()
  next()
})

router.afterEach(() => {
  NProgress.done()
})

router.onError(() => {
  NProgress.done()
})

router.beforeEach(async (to, from, next) => {
  const { getUser } = useGlobalStore()
  const { user } = storeToRefs(useGlobalStore())

  // Profile has no meta.roles, so previously skipped getUser — leaving userRole empty and
  // mounting the applicant DriverApplication flow for staff after a refresh/login race.
  const isAppRoute = to.matched.some((record) => record.name === 'admin')
  if (isAppRoute && !user.value.id && to.name !== 'login') {
    await getUser()
  }

  if (!to.meta?.roles) return next()

  if (to.name !== 'login' && !user.value.id) next({ name: 'login' })
  else if (!to.meta.roles || to.meta?.roles.some((role: string) => user.value[role as unknown as keyof User])) {
    next()
  } else {
    next({ name: 'profile' })
  }
})

export default router
